import { useState } from 'react';
import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import { AlertTriangle, ArrowDownRight, ArrowUpRight, ShieldAlert, ShieldCheck } from 'lucide-react';
import { formatMoney, formatQty, PAYMENT_METHODS, type CurrencyCode, type PaymentMethod, type Permission } from '@tengesa/shared';
import { api } from '../../lib/api';
import { useSession } from '../../app/session';
import { useCurrentBranch } from '../../app/Shell';
import { Badge, ErrorNotice, PageHeader, Panel, Spinner } from '../../ui';
import { BarList, ColumnChart } from '../../ui/charts';

interface Totals {
  saleCount: number;
  netSalesCents: number;
  revenueCents: number;
  grossProfitCents: number;
  marginBp: number;
  averageSaleCents: number;
  returnsCents: number;
}
interface Dashboard {
  today: string;
  showsProfit: boolean;
  sales: {
    today: Totals;
    yesterday: Totals;
    week: Totals;
    trend: { date: string; saleCount: number; netSalesCents: number; grossProfitCents: number }[];
    topProducts: { productId: string; name: string; unit: string; qtyMilli: number; netSalesCents: number; grossProfitCents: number }[];
    byMethodToday: { method: string; amountCents: number }[];
  };
  stock: {
    valueCents: number | null;
    lowStockCount: number;
    outOfStockCount: number;
    lowStock: { productId: string; name: string; unit: string; branch: string; qtyMilli: number; reorderLevelMilli: number }[];
    discrepancy30dCents: number;
  };
  cash: { openShifts: number; awaitingSignOff: number; variance7d: Record<string, number>; shortShifts7d: number };
  debtors: { totalCents: number; overdueCents: number; customers: number; topOverdue: { id: string; name: string; balanceCents: number; ageing: { overdue: number } }[] } | null;
  creditors: { totalCents: number; overdueCents: number; dueThisWeekCents: number } | null;
  expenses: { pendingCount: number; pendingCents: number };
  auditIntegrity: { ok: boolean; checked: number; firstBrokenSeq: number | null } | null;
}

function greeting() {
  const h = Number(new Intl.DateTimeFormat('en-GB', { hour: 'numeric', hour12: false, timeZone: 'Africa/Harare' }).format(new Date()));
  return h < 12 ? 'Mangwanani' : h < 17 ? 'Masikati' : 'Manheru';
}

export function DashboardPage() {
  const { me, can } = useSession();
  const { branch } = useCurrentBranch();
  const [scope, setScope] = useScope();
  const cur = me.business.baseCurrency as CurrencyCode;
  const money = (c: number) => formatMoney(c, cur);
  const q = useQuery({
    queryKey: ['dashboard', scope === 'branch' ? branch?.id : 'all'],
    queryFn: () => api.get<Dashboard>(`/api/dashboard${scope === 'branch' && branch ? `?branchId=${branch.id}` : ''}`),
    refetchInterval: 60_000,
  });
  const first = me.user.fullName.split(' ')[0];

  return (
    <>
      <PageHeader
        title={`${greeting()}, ${first}`}
        description={`How ${me.business.tradingName ?? me.business.name} is doing today.`}
        actions={
          me.branches.length > 1 && (
            <div className="inline-flex rounded-[8px] border border-line-strong bg-surface p-0.5" role="group" aria-label="Which branches">
              {(['all', 'branch'] as const).map((s) => (
                <button
                  key={s}
                  onClick={() => setScope(s)}
                  aria-pressed={scope === s}
                  className={clsx('rounded-[6px] px-3 py-1.5 text-sm font-medium', scope === s ? 'bg-ledger-600 text-white' : 'text-muted hover:text-ink')}
                >
                  {s === 'all' ? 'All branches' : (branch?.name ?? 'This branch')}
                </button>
              ))}
            </div>
          )
        }
      />
      {q.isLoading && <Spinner />}
      {q.error && <ErrorNotice error={q.error} onRetry={() => q.refetch()} />}
      {q.data && <Body d={q.data} money={money} can={can} />}
    </>
  );
}

function useScope(): ['all' | 'branch', (s: 'all' | 'branch') => void] {
  const key = 'tengesa.dashboardScope';
  let initial: 'all' | 'branch' = 'all';
  try {
    if (localStorage.getItem(key) === 'branch') initial = 'branch';
  } catch {
    /* ignore */
  }
  const [v, setV] = useState(initial);
  return [
    v,
    (s) => {
      setV(s);
      try {
        localStorage.setItem(key, s);
      } catch {
        /* ignore */
      }
    },
  ];
}

function Body({ d, money, can }: { d: Dashboard; money: (c: number) => string; can: (p: Permission) => boolean }) {
  const t = d.sales.today;
  const y = d.sales.yesterday;
  const change = y.netSalesCents ? Math.round(((t.netSalesCents - y.netSalesCents) / y.netSalesCents) * 100) : null;
  const variance = Object.entries(d.cash.variance7d);
  const alerts: { text: string; to: string; tone: 'warn' | 'danger' }[] = [];
  if (d.auditIntegrity && !d.auditIntegrity.ok) alerts.push({ text: 'The audit log has been tampered with', to: '/audit', tone: 'danger' });
  if (d.stock.outOfStockCount) alerts.push({ text: `${d.stock.outOfStockCount} product${d.stock.outOfStockCount === 1 ? ' is' : 's are'} out of stock`, to: '/products', tone: 'warn' });
  if (d.cash.awaitingSignOff) alerts.push({ text: `${d.cash.awaitingSignOff} closed shift${d.cash.awaitingSignOff === 1 ? '' : 's'} waiting to be signed off`, to: '/shifts', tone: 'warn' });
  if (d.expenses.pendingCount) alerts.push({ text: `${d.expenses.pendingCount} expense${d.expenses.pendingCount === 1 ? '' : 's'} (${money(d.expenses.pendingCents)}) waiting for approval`, to: '/expenses', tone: 'warn' });
  if (d.debtors?.overdueCents) alerts.push({ text: `${money(d.debtors.overdueCents)} owed by customers is overdue`, to: '/customers', tone: 'warn' });
  if (d.creditors?.overdueCents) alerts.push({ text: `${money(d.creditors.overdueCents)} owed to suppliers is overdue`, to: '/suppliers', tone: 'danger' });

  return (
    <div className="space-y-6">
      {/* Today */}
      <section className="grid gap-px overflow-hidden rounded-[var(--radius-panel)] border border-line bg-line sm:grid-cols-2 lg:grid-cols-4" aria-label="Today">
        <div className="bg-surface p-5 sm:col-span-2 lg:col-span-1">
          <p className="text-sm text-muted">Sales today</p>
          <p className="mt-1 text-[38px] font-semibold leading-none num">{money(t.netSalesCents)}</p>
          <p className="mt-2 flex items-center gap-1 text-sm text-muted">
            {change != null && (
              <span className={clsx('inline-flex items-center font-medium', change >= 0 ? 'text-ok' : 'text-danger')}>
                {change >= 0 ? <ArrowUpRight className="size-4" /> : <ArrowDownRight className="size-4" />}
                {Math.abs(change)}%
              </span>
            )}
            vs {money(y.netSalesCents)} yesterday
          </p>
        </div>
        <Kpi label="Gross profit today" value={d.showsProfit ? money(t.grossProfitCents) : '—'} note={d.showsProfit ? `${(t.marginBp / 100).toFixed(1)}% margin on ${money(t.revenueCents)} excl. VAT` : 'Needs the profit report permission'} />
        <Kpi label="Customers served" value={String(t.saleCount)} note={`Average sale ${money(t.averageSaleCents)}`} />
        <Kpi label="Last 7 days" value={money(d.sales.week.netSalesCents)} note={d.showsProfit ? `${money(d.sales.week.grossProfitCents)} gross profit` : `${d.sales.week.saleCount} sales`} />
      </section>

      {alerts.length > 0 && (
        <Panel title="Needs attention">
          <ul className="divide-y divide-line">
            {alerts.map((a) => (
              <li key={a.text}>
                <Link to={a.to} className="flex items-center gap-3 px-5 py-3 hover:bg-ledger-50/60">
                  <AlertTriangle className={clsx('size-4 shrink-0', a.tone === 'danger' ? 'text-danger' : 'text-warn')} aria-hidden />
                  <span>{a.text}</span>
                </Link>
              </li>
            ))}
          </ul>
        </Panel>
      )}

      <div className="grid gap-6 lg:grid-cols-[1.6fr_1fr]">
        <Panel title="Sales, last 14 days">
          <div className="p-5">
            <ColumnChart
              label="Net sales per day for the last 14 days"
              data={d.sales.trend.map((x) => ({
                key: x.date,
                label: new Date(`${x.date}T12:00:00Z`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }),
                value: x.netSalesCents,
              }))}
              format={money}
              detail={(i) => (
                <p className="text-muted">
                  {d.sales.trend[i]!.saleCount} sales{d.showsProfit ? `, ${money(d.sales.trend[i]!.grossProfitCents)} profit` : ''}
                </p>
              )}
            />
          </div>
        </Panel>
        <Panel title="How customers paid today">
          <div className="p-5">
            {d.sales.byMethodToday.length === 0 ? (
              <p className="text-muted">No sales yet today.</p>
            ) : (
              <BarList
                format={money}
                items={d.sales.byMethodToday.filter((m) => m.amountCents !== 0).map((m) => ({ label: PAYMENT_METHODS[m.method as PaymentMethod]?.label.split(' (')[0] ?? m.method, value: m.amountCents }))}
              />
            )}
          </div>
        </Panel>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <Panel title="Best sellers, last 7 days" className="lg:col-span-2">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-line text-sm text-muted">
                <th className="px-5 py-2 font-medium">Product</th>
                <th className="px-3 py-2 text-right font-medium">Sold</th>
                <th className="px-3 py-2 text-right font-medium">Sales</th>
                {d.showsProfit && <th className="px-5 py-2 text-right font-medium">Profit</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-line">
              {d.sales.topProducts.map((p) => (
                <tr key={p.productId}>
                  <td className="px-5 py-2">{p.name}</td>
                  <td className="px-3 py-2 text-right num">{formatQty(p.qtyMilli)}</td>
                  <td className="px-3 py-2 text-right num">{money(p.netSalesCents)}</td>
                  {d.showsProfit && <td className="px-5 py-2 text-right num">{money(p.grossProfitCents)}</td>}
                </tr>
              ))}
              {!d.sales.topProducts.length && (
                <tr>
                  <td className="px-5 py-6 text-muted" colSpan={4}>No sales in the last 7 days.</td>
                </tr>
              )}
            </tbody>
          </table>
        </Panel>
        <Panel title="Stock" actions={<Link to="/products" className="text-sm font-medium text-ledger-600 hover:underline">Products</Link>}>
          <dl className="grid grid-cols-2 gap-px bg-line">
            <Mini label="Stock value" value={d.stock.valueCents != null ? money(d.stock.valueCents) : '—'} />
            <Mini label="Losses, 30 days" value={money(-d.stock.discrepancy30dCents)} tone={d.stock.discrepancy30dCents < 0 ? 'danger' : undefined} />
          </dl>
          <p className="border-t border-line px-5 pt-3 text-sm font-medium">
            {d.stock.lowStockCount} item{d.stock.lowStockCount === 1 ? '' : 's'} to reorder
          </p>
          <ul className="px-5 pb-3">
            {d.stock.lowStock.map((l) => (
              <li key={`${l.productId}-${l.branch}`} className="flex justify-between gap-3 py-1 text-sm">
                <span className="truncate">{l.name} <span className="text-muted">({l.branch})</span></span>
                <span className={clsx('num', l.qtyMilli <= 0 ? 'text-danger' : 'text-warn')}>{formatQty(l.qtyMilli)}</span>
              </li>
            ))}
          </ul>
        </Panel>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {d.debtors && (
          <Panel title="Customers owe you" actions={<Link to="/customers" className="text-sm font-medium text-ledger-600 hover:underline">Customers</Link>}>
            <dl className="grid grid-cols-2 gap-px bg-line">
              <Mini label="Total" value={money(d.debtors.totalCents)} />
              <Mini label="Overdue" value={money(d.debtors.overdueCents)} tone={d.debtors.overdueCents ? 'danger' : undefined} />
            </dl>
            <ul className="divide-y divide-line border-t border-line">
              {d.debtors.topOverdue.map((c) => (
                <li key={c.id}>
                  <Link to={`/customers/${c.id}`} className="flex justify-between gap-3 px-5 py-2 text-sm hover:bg-ledger-50/60">
                    <span className="truncate">{c.name}</span>
                    <span className="num text-danger">{money(c.ageing.overdue)} overdue</span>
                  </Link>
                </li>
              ))}
            </ul>
          </Panel>
        )}
        {d.creditors && (
          <Panel title="You owe suppliers" actions={<Link to="/suppliers" className="text-sm font-medium text-ledger-600 hover:underline">Suppliers</Link>}>
            <dl className="grid grid-cols-2 gap-px bg-line">
              <Mini label="Total" value={money(d.creditors.totalCents)} />
              <Mini label="Due this week" value={money(d.creditors.dueThisWeekCents)} />
              <Mini label="Overdue" value={money(d.creditors.overdueCents)} tone={d.creditors.overdueCents ? 'danger' : undefined} wide />
            </dl>
          </Panel>
        )}
        <Panel title="Cash" actions={<Link to="/shifts" className="text-sm font-medium text-ledger-600 hover:underline">Shifts</Link>}>
          <dl className="grid grid-cols-2 gap-px bg-line">
            <Mini label="Tills open now" value={String(d.cash.openShifts)} />
            <Mini label="Short shifts, 7 days" value={String(d.cash.shortShifts7d)} tone={d.cash.shortShifts7d ? 'danger' : undefined} />
          </dl>
          <div className="border-t border-line px-5 py-3 text-sm">
            {variance.length === 0 ? (
              <span className="text-muted">Every counted till matched in the last 7 days.</span>
            ) : (
              variance.map(([c, v]) => (
                <p key={c}>
                  Net {v < 0 ? 'short' : 'over'} <span className={clsx('num font-medium', v < 0 ? 'text-danger' : 'text-ok')}>{formatMoney(Math.abs(v), c as CurrencyCode)}</span> in 7 days
                </p>
              ))
            )}
          </div>
          {d.auditIntegrity && (
            <p className="flex items-center gap-2 border-t border-line px-5 py-3 text-sm">
              {d.auditIntegrity.ok ? <ShieldCheck className="size-4 text-ok" /> : <ShieldAlert className="size-4 text-danger" />}
              Audit log {d.auditIntegrity.ok ? 'intact' : 'tampered'} <Badge>{d.auditIntegrity.checked.toLocaleString()} entries</Badge>
            </p>
          )}
        </Panel>
      </div>
      {can('audit.view') && (
        <p className="text-sm text-muted">
          Staff sign-ins and recent changes are under <Link to="/activity" className="font-medium text-ledger-600 hover:underline">Security and activity</Link>.
        </p>
      )}
    </div>
  );
}

function Kpi({ label, value, note }: { label: string; value: string; note: string }) {
  return (
    <div className="bg-surface p-5">
      <p className="text-sm text-muted">{label}</p>
      <p className="mt-1 text-[26px] font-semibold leading-tight num">{value}</p>
      <p className="mt-2 text-sm text-muted">{note}</p>
    </div>
  );
}

function Mini({ label, value, tone, wide }: { label: string; value: string; tone?: 'danger'; wide?: boolean }) {
  return (
    <div className={clsx('bg-surface px-5 py-3', wide && 'col-span-2')}>
      <dt className="text-sm text-muted">{label}</dt>
      <dd className={clsx('text-xl font-semibold num', tone === 'danger' && 'text-danger')}>{value}</dd>
    </div>
  );
}
